import React from 'react';
import {
  DatePicker,
  InputNumber,
  Input,
  Radio,
  Form,
  Typography,
  Tooltip,
  Select,
  Row,
  Col,
} from 'antd';
import { WrapperSelect } from 'common/components';
import {
  SelectProductImageUrlInput,
  SelectProductImageUrlInputMultiple,
} from './select-product-image-url-input';
import { ServerErrorIndicator } from './ServerErrorIndicator';

import { QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as constants from 'static/Constants';
import { makeRules } from './utils';
import { getPrecisionFromStep } from 'pages/product-full-view/components/product-new-detail-view/walmart-modules/utils';

const { Text } = Typography;

export const PostField = (props) => {
  const {
    //* input
    label,
    min,
    max,
    minLength,
    maxLength,
    //* schema
    type,
    fullKey,
    enums,
    description,
    isRequiredProperty,
    //* additional
    className,
    labelCol,
    wrapperCol,
    serverError,
    showServerError,
    name,
    minItems,
    ruleType,
    productId,
    filterOption,
    isRivirMappingField,
    rivirMappingField,
    step,
  } = props;

  const fieldName =
    name !== null && name !== undefined ? name : fullKey?.split('.');

  return (
    <Form.Item
      name={fieldName}
      label={
        label && (
          <div>
            <Tooltip title={label}>
              <Text ellipsis strong>
                {label}
              </Text>
            </Tooltip>
            {isRivirMappingField && (
              <Tooltip
                title={
                  rivirMappingField
                    ? `Auto populate from RIVIR: ${rivirMappingField}`
                    : ''
                }
                color='#238dd9'
              >
                <InfoCircleOutlined style={{ margin: '0px 4px' }} />
              </Tooltip>
            )}
            {description && (
              <Tooltip title={description} color='#238dd9'>
                <QuestionCircleOutlined style={{ margin: '0px 4px' }} />
              </Tooltip>
            )}
          </div>
        )
      }
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      className={className}
      rules={makeRules({ isRequiredProperty, ruleType, minItems })}
      help={serverError && <Text type='danger'> {serverError?.error} </Text>}
    >
      {showServerError ? (
        <PostFieldInputWrapper serverError={serverError}>
          <PostFieldInput
            type={type}
            enums={enums}
            min={min}
            max={max}
            minLength={minLength}
            maxLength={maxLength}
            productId={productId}
            fullKey={fullKey}
            filterOption={filterOption}
            step={step}
          />
        </PostFieldInputWrapper>
      ) : (
        <PostFieldInput
          type={type}
          enums={enums}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          productId={productId}
          fullKey={fullKey}
          filterOption={filterOption}
          step={step}
        />
      )}
    </Form.Item>
  );
};

const PostFieldInput = (props) => {
  const {
    type,
    enums,
    fullKey,
    productId,
    filterOption,
    step,
    ...restProps
  } = props;

  if (type === 'dateTime') {
    return (
      <DatePicker
        format={constants.DATE_FORMAT.SYSTEM_FORMAT}
        style={{ minWidth: 200 }}
        {...restProps}
      />
    );
  }

  if (type === 'number') {
    const precision = getPrecisionFromStep(step);
    return (
      <InputNumber
        placeholder={`Please Input number`}
        style={{ minWidth: 200 }}
        precision={precision}
        step={step}
        {...restProps}
      />
    );
  }

  if (type === 'integer') {
    const precision = getPrecisionFromStep(step);
    return (
      <InputNumber
        placeholder={`Please Input integer number`}
        parser={(value) => value.replace(/\.\d*/, '')}
        style={{ minWidth: 200 }}
        precision={precision}
        step={step}
        {...restProps}
      />
    );
  }

  if (type === 'yes/no') {
    return (
      <Radio.Group {...restProps}>
        <Radio value={'yes'}>Yes</Radio>
        <Radio value={'no'}>No</Radio>
      </Radio.Group>
    );
  }

  if (type === 'true/false') {
    return (
      <Radio.Group {...restProps}>
        <Radio value={true}>True</Radio>
        <Radio value={false}>False</Radio>
      </Radio.Group>
    );
  }

  if (type === 'select') {
    if (enums) {
      return (
        <WrapperSelect
          showSearch
          placeholder={`Please Select value`}
          filterOption={filterOption}
          {...restProps}
        >
          {enums
            ?.slice()
            .sort()
            .map((enumValue, index) => (
              <Select.Option value={enumValue} key={index}>
                {enumValue}
              </Select.Option>
            ))}
        </WrapperSelect>
      );
    }
  }

  if (type === 'multi-select') {
    if (!!enums) {
      return (
        <WrapperSelect
          showSearch
          placeholder={`Please Select value`}
          mode='multiple'
          filterOption={filterOption}
          {...restProps}
        >
          {enums
            ?.slice()
            .sort()
            .map((enumValue, index) => (
              <Select.Option value={enumValue} key={index}>
                {enumValue}
              </Select.Option>
            ))}
        </WrapperSelect>
      );
    }
  }

  if (type === 'product-image-url') {
    return <SelectProductImageUrlInput {...restProps} productId={productId} />;
  }

  if (type === 'product-image-url-multiple') {
    return (
      <SelectProductImageUrlInputMultiple
        {...restProps}
        productId={productId}
      />
    );
  }

  return (
    <Input
      placeholder={`Please Input text`}
      autoFocus
      style={{ width: '100%' }}
      {...restProps}
    />
  );
};

const PostFieldInputWrapper = ({ children, serverError, ...restProps }) => {
  const renderChildren = () => {
    return React.Children.map(children, (child) => {
      return React.cloneElement(child, restProps);
    });
  };

  return (
    <Row align='middle'>
      <Col style={{ flex: 1, overflow: 'hidden' }}>{renderChildren()}</Col>
      <Col>
        <ServerErrorIndicator serverError={serverError} />
      </Col>
    </Row>
  );
};
